/**
 * PWA Install Guide v2.0
 * 
 * Provides ACCURATE installation instructions based on device/browser
 * - Detects iOS Safari, Chrome, Firefox, Edge, Samsung Internet
 * - Shows step-by-step instructions with visuals
 * - No fake loading bars - only real status indicators
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
    X, Download, Share, Plus, MoreVertical, 
    Smartphone, Monitor, CheckCircle2, ChevronRight,
    ExternalLink, Home, Menu
} from 'lucide-react';

interface PWAInstallGuideProps {
    isVisible: boolean;
    isDarkMode: boolean;
    onClose: () => void;
    onInstallClick?: () => Promise<boolean>;
    isInstallable?: boolean;
}

type DeviceType = 'ios' | 'android' | 'desktop';
type BrowserType = 'safari' | 'chrome' | 'firefox' | 'edge' | 'samsung' | 'other';

interface InstallStep {
    icon: React.ReactNode;
    title: string;
    description: string;
    highlight?: string;
}

const PWAInstallGuide: React.FC<PWAInstallGuideProps> = ({
    isVisible,
    isDarkMode,
    onClose,
    onInstallClick,
    isInstallable = false
}) => {
    const [device, setDevice] = useState<DeviceType>('desktop');
    const [browser, setBrowser] = useState<BrowserType>('other');
    const [currentStep, setCurrentStep] = useState(0);
    const [isInstalling, setIsInstalling] = useState(false);
    const [installSuccess, setInstallSuccess] = useState(false);
    const [showManualSteps, setShowManualSteps] = useState(false);

    // Detect device and browser
    useEffect(() => {
        const ua = navigator.userAgent.toLowerCase();
        
        // Detect device
        if (/ipad|iphone|ipod/.test(ua) && !(window as any).MSStream) {
            setDevice('ios');
        } else if (/android/.test(ua)) {
            setDevice('android');
        } else {
            setDevice('desktop');
        }

        // Detect browser
        if (/safari/.test(ua) && !/chrome/.test(ua) && !/crios/.test(ua)) {
            setBrowser('safari');
        } else if (/samsungbrowser/.test(ua)) {
            setBrowser('samsung');
        } else if (/edg/.test(ua)) {
            setBrowser('edge');
        } else if (/firefox|fxios/.test(ua)) {
            setBrowser('firefox');
        } else if (/chrome|crios/.test(ua)) {
            setBrowser('chrome');
        } else {
            setBrowser('other');
        }
    }, []);

    // Handle native install prompt
    const handleInstallClick = useCallback(async () => {
        if (!onInstallClick) {
            setShowManualSteps(true);
            return;
        }

        setIsInstalling(true);
        try {
            const success = await onInstallClick();
            if (success) {
                setInstallSuccess(true);
                setTimeout(() => onClose(), 2000);
            } else {
                // User dismissed prompt or not supported - show manual steps
                setShowManualSteps(true);
            }
        } catch (error) {
            setShowManualSteps(true);
        } finally {
            setIsInstalling(false);
        }
    }, [onInstallClick, onClose]);

    // Get installation steps based on device/browser
    const getInstallSteps = (): InstallStep[] => {
        if (device === 'ios') {
            return [
                {
                    icon: <Share className="w-5 h-5" />,
                    title: 'Tap the Share button',
                    description: 'At the bottom of Safari (the square with arrow pointing up)',
                    highlight: 'Share'
                },
                {
                    icon: <Plus className="w-5 h-5" />,
                    title: 'Scroll down and tap "Add to Home Screen"',
                    description: 'You may need to scroll down in the share menu',
                    highlight: 'Add to Home Screen'
                },
                {
                    icon: <CheckCircle2 className="w-5 h-5" />,
                    title: 'Tap "Add" to confirm',
                    description: 'Luca will appear on your home screen like a native app',
                    highlight: 'Add'
                }
            ];
        }

        if (device === 'android') {
            if (browser === 'chrome') {
                return [
                    {
                        icon: <MoreVertical className="w-5 h-5" />,
                        title: 'Tap the menu button',
                        description: 'Three dots (⋮) in the top-right corner of Chrome',
                        highlight: 'Menu (⋮)'
                    },
                    {
                        icon: <Download className="w-5 h-5" />,
                        title: 'Tap "Install app" or "Add to Home screen"',
                        description: 'Chrome will prompt you to install Luca',
                        highlight: 'Install app'
                    },
                    {
                        icon: <CheckCircle2 className="w-5 h-5" />,
                        title: 'Tap "Install" to confirm',
                        description: 'Luca will appear in your app drawer',
                        highlight: 'Install'
                    }
                ];
            }
            if (browser === 'samsung') {
                return [
                    {
                        icon: <Menu className="w-5 h-5" />,
                        title: 'Tap the menu button',
                        description: 'Three lines (☰) at the bottom-right of Samsung Internet',
                        highlight: 'Menu (☰)'
                    },
                    {
                        icon: <Plus className="w-5 h-5" />,
                        title: 'Tap "Add page to" → "Home screen"',
                        description: 'Or look for "Install" option if available',
                        highlight: 'Home screen'
                    },
                    {
                        icon: <CheckCircle2 className="w-5 h-5" />,
                        title: 'Tap "Add" to confirm',
                        description: 'Luca will appear on your home screen',
                        highlight: 'Add'
                    }
                ];
            }
            // Generic Android
            return [
                {
                    icon: <MoreVertical className="w-5 h-5" />,
                    title: 'Open browser menu',
                    description: 'Usually three dots (⋮) or lines (☰)',
                    highlight: 'Menu'
                },
                {
                    icon: <Home className="w-5 h-5" />,
                    title: 'Find "Add to Home screen" or "Install"',
                    description: 'The option name varies by browser',
                    highlight: 'Add to Home screen'
                },
                {
                    icon: <CheckCircle2 className="w-5 h-5" />,
                    title: 'Confirm the installation',
                    description: 'Luca will be added to your app drawer',
                    highlight: 'Confirm'
                }
            ];
        }

        // Desktop
        if (browser === 'chrome' || browser === 'edge') {
            return [
                {
                    icon: <Download className="w-5 h-5" />,
                    title: 'Look for the install icon',
                    description: 'In the address bar (right side), look for a computer/plus icon',
                    highlight: 'Install icon'
                },
                {
                    icon: <ExternalLink className="w-5 h-5" />,
                    title: 'Click "Install"',
                    description: 'A popup will appear asking to install Luca',
                    highlight: 'Install'
                },
                {
                    icon: <CheckCircle2 className="w-5 h-5" />,
                    title: 'Launch from desktop or start menu',
                    description: 'Luca will open in its own window like a native app',
                    highlight: 'Desktop'
                }
            ];
        }

        // Firefox or other
        return [
            {
                icon: <MoreVertical className="w-5 h-5" />,
                title: 'Open browser menu',
                description: 'Click the menu button (usually three lines or dots)',
                highlight: 'Menu'
            },
            {
                icon: <Plus className="w-5 h-5" />,
                title: 'Look for "Install" or "Add to Home"',
                description: 'Some browsers may not support PWA installation',
                highlight: 'Install'
            },
            {
                icon: <CheckCircle2 className="w-5 h-5" />,
                title: 'Confirm installation',
                description: 'Luca will be installed as an app',
                highlight: 'Confirm'
            }
        ];
    };

    const steps = getInstallSteps();

    const getDeviceIcon = () => {
        switch (device) {
            case 'ios':
            case 'android':
                return <Smartphone className="w-6 h-6" />;
            default:
                return <Monitor className="w-6 h-6" />;
        }
    };

    const getDeviceName = () => {
        switch (device) {
            case 'ios': return 'iPhone/iPad';
            case 'android': return 'Android';
            default: return 'Desktop';
        }
    };

    const getBrowserName = () => {
        switch (browser) {
            case 'safari': return 'Safari';
            case 'chrome': return 'Chrome';
            case 'firefox': return 'Firefox';
            case 'edge': return 'Edge';
            case 'samsung': return 'Samsung Internet';
            default: return 'Browser';
        }
    };

    if (!isVisible) return null;

    return (
        <div 
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-md animate-in fade-in duration-200"
            style={{
                padding: 'max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left))'
            }}
        >
            <div 
                className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden transform animate-in zoom-in-95 duration-300 ${
                    isDarkMode 
                        ? 'bg-slate-900 border border-slate-800' 
                        : 'bg-white border border-slate-200'
                }`}
                style={{
                    maxHeight: 'calc(100dvh - 2rem)',
                }}
            >
                {/* Header */}
                <div className={`relative px-6 py-5 border-b ${
                    isDarkMode ? 'border-slate-800 bg-slate-800/50' : 'border-slate-200 bg-blue-50'
                }`}>
                    <button
                        onClick={onClose}
                        className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                            isDarkMode 
                                ? 'hover:bg-slate-700 text-slate-400' 
                                : 'hover:bg-slate-200 text-slate-500'
                        }`}
                    >
                        <X size={20} />
                    </button>

                    <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl overflow-hidden border-2 shadow-lg ${
                            isDarkMode ? 'border-slate-700' : 'border-white'
                        }`}>
                            <img 
                                src="/robot-luca.png" 
                                alt="Luca" 
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.src = '/app-icon.png'; }}
                            />
                        </div>
                        <div>
                            <h2 className={`text-xl font-bold ${
                                isDarkMode ? 'text-white' : 'text-slate-900'
                            }`}>
                                Install Luca
                            </h2>
                            <p className={`text-sm flex items-center gap-2 ${
                                isDarkMode ? 'text-slate-400' : 'text-slate-600'
                            }`}>
                                {getDeviceIcon()}
                                <span>{getDeviceName()} • {getBrowserName()}</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                    {/* Success State */}
                    {installSuccess && (
                        <div className="text-center py-8">
                            <div className="inline-flex p-4 bg-green-500 rounded-2xl mb-4">
                                <CheckCircle2 className="w-10 h-10 text-white" />
                            </div>
                            <h3 className={`text-lg font-bold mb-2 ${
                                isDarkMode ? 'text-white' : 'text-slate-900'
                            }`}>
                                Installation Started!
                            </h3>
                            <p className={`text-sm ${
                                isDarkMode ? 'text-slate-400' : 'text-slate-600'
                            }`}>
                                Luca is being added to your {device === 'desktop' ? 'desktop' : 'home screen'}
                            </p>
                        </div>
                    )}

                    {/* Native Install Button (when available) */}
                    {!installSuccess && isInstallable && !showManualSteps && (
                        <div className="mb-6">
                            <button
                                onClick={handleInstallClick}
                                disabled={isInstalling}
                                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isInstalling ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Installing...
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-5 h-5" />
                                        Install Luca Now
                                    </>
                                )}
                            </button>
                            
                            <button
                                onClick={() => setShowManualSteps(true)}
                                className={`w-full mt-3 text-sm text-center py-2 ${
                                    isDarkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                Show manual installation steps
                            </button>
                        </div>
                    )}

                    {/* Manual Installation Steps */}
                    {!installSuccess && (showManualSteps || !isInstallable) && (
                        <>
                            <div className={`mb-4 p-3 rounded-xl ${
                                isDarkMode ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-yellow-50 border border-yellow-200'
                            }`}>
                                <p className={`text-sm font-medium ${
                                    isDarkMode ? 'text-yellow-400' : 'text-yellow-700'
                                }`}>
                                    {device === 'ios' 
                                        ? '📱 iOS requires manual installation through Safari'
                                        : '📋 Follow these steps to install Luca:'
                                    }
                                </p>
                            </div>

                            <div className="space-y-4">
                                {steps.map((step, index) => (
                                    <div 
                                        key={index}
                                        className={`flex items-start gap-4 p-4 rounded-xl transition-all cursor-pointer ${
                                            currentStep === index
                                                ? isDarkMode 
                                                    ? 'bg-blue-500/10 border border-blue-500/30' 
                                                    : 'bg-blue-50 border border-blue-200'
                                                : isDarkMode
                                                    ? 'bg-slate-800/50 hover:bg-slate-800'
                                                    : 'bg-slate-50 hover:bg-slate-100'
                                        }`}
                                        onClick={() => setCurrentStep(index)}
                                    >
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                            currentStep === index
                                                ? 'bg-blue-500 text-white'
                                                : isDarkMode
                                                    ? 'bg-slate-700 text-slate-300'
                                                    : 'bg-slate-200 text-slate-600'
                                        }`}>
                                            <span className="font-bold">{index + 1}</span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className={`font-semibold ${
                                                    isDarkMode ? 'text-white' : 'text-slate-900'
                                                }`}>
                                                    {step.title}
                                                </h4>
                                                {step.icon}
                                            </div>
                                            <p className={`text-sm mt-1 ${
                                                isDarkMode ? 'text-slate-400' : 'text-slate-600'
                                            }`}>
                                                {step.description}
                                            </p>
                                            {step.highlight && (
                                                <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
                                                    isDarkMode 
                                                        ? 'bg-blue-500/20 text-blue-300' 
                                                        : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    Look for: "{step.highlight}"
                                                </span>
                                            )}
                                        </div>
                                        <ChevronRight className={`w-5 h-5 flex-shrink-0 ${
                                            isDarkMode ? 'text-slate-600' : 'text-slate-400'
                                        }`} />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Benefits */}
                    {!installSuccess && (
                        <div className={`mt-6 pt-6 border-t ${
                            isDarkMode ? 'border-slate-800' : 'border-slate-200'
                        }`}>
                            <h4 className={`text-sm font-semibold mb-3 ${
                                isDarkMode ? 'text-slate-300' : 'text-slate-700'
                            }`}>
                                Why install Luca?
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { emoji: '⚡', text: 'Faster loading' },
                                    { emoji: '📴', text: 'Works offline' },
                                    { emoji: '🔔', text: 'Notifications' },
                                    { emoji: '💾', text: 'Saves data' }
                                ].map((benefit, i) => (
                                    <div 
                                        key={i}
                                        className={`flex items-center gap-2 p-2 rounded-lg ${
                                            isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50'
                                        }`}
                                    >
                                        <span className="text-lg">{benefit.emoji}</span>
                                        <span className={`text-sm ${
                                            isDarkMode ? 'text-slate-300' : 'text-slate-600'
                                        }`}>
                                            {benefit.text}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {!installSuccess && (
                    <div className={`px-6 py-4 border-t ${
                        isDarkMode ? 'border-slate-800 bg-slate-800/30' : 'border-slate-200 bg-slate-50'
                    }`}>
                        <button
                            onClick={onClose}
                            className={`w-full py-3 rounded-xl font-medium transition-colors ${
                                isDarkMode 
                                    ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' 
                                    : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                            }`}
                        >
                            Maybe Later
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PWAInstallGuide;

